import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Clock, Menu, Phone, Siren, X } from "lucide-react";
import { Logo } from "./Logo";
import { SERVICES } from "@/data/services";
import { SITE } from "@/data/site";

export const goToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -110 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const SectionLink = ({ id, label, className = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const onClick = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: id } });
    else goToSection(id);
  };
  return (
    <a
      href={`/#${id}`}
      onClick={onClick}
      className={`text-sm font-semibold text-slate-700 transition-colors hover:text-brand-red ${className}`}
      data-testid={`nav-section-${id}`}
    >
      {label}
    </a>
  );
};

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      {/* Top utility bar */}
      <div className="bg-brand-ink text-white">
        <div className="container-x flex items-center justify-between py-1.5 text-xs">
          <p className="flex items-center gap-2 font-medium">
            <Clock className="h-3.5 w-3.5 text-brand-red" /> {SITE.hours}
          </p>
          <a href={`tel:${SITE.phoneEmergency}`} className="flex items-center gap-2 font-semibold" data-testid="topbar-emergency">
            <Siren className="h-3.5 w-3.5" /> 24/7 emergency calls: {SITE.phoneEmergencyDisplay}
          </a>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/95 backdrop-blur-xl">
        <div className="container-x flex items-center justify-between py-3">
          <Link to="/" data-testid="nav-logo-link" aria-label="Fusion Glass home">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            <div className="group relative">
              <button
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 transition-colors hover:text-brand-red"
                data-testid="nav-services-dropdown"
              >
                Services <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
              </button>
              <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                <div className="grid w-[560px] grid-cols-2 gap-1 rounded-2xl border border-slate-100 bg-white p-3 shadow-2xl">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      className="rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-brand-red/5 hover:text-brand-red"
                      data-testid={`nav-service-${s.slug}`}
                    >
                      {s.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <SectionLink id="why" label="Why Fusion" />
            <SectionLink id="regions" label="Regions" />
            <SectionLink id="reviews" label="Reviews" />
            <Link to="/contact" className="text-sm font-semibold text-slate-700 transition-colors hover:text-brand-red" data-testid="nav-contact-link">
              Contact
            </Link>
          </nav>

          <div className="flex items-center gap-2.5">
            <a
              href={`tel:${SITE.phoneMain}`}
              className="hidden items-center gap-2 rounded-full border-2 border-slate-200 px-4 py-2 text-sm font-bold text-slate-900 transition-colors hover:border-brand-red hover:text-brand-red sm:flex"
              data-testid="nav-call-btn"
            >
              <Phone className="h-4 w-4 text-brand-red" />
              {SITE.phoneMainDisplay}
            </a>
            <Link
              to="/contact"
              className="rounded-full bg-brand-red px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-white transition-all hover:bg-brand-reddark hover:shadow-[0_10px_30px_rgba(225,29,46,0.35)] whitespace-nowrap"
              data-testid="nav-quote-btn"
            >
              Get a free quote
            </Link>
            <button
              onClick={() => setOpen(!open)}
              className="rounded-full border border-slate-200 p-2 text-slate-700 lg:hidden"
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-white px-6 pb-10 pt-24 lg:hidden" data-testid="nav-mobile-menu">
          <p className="eyebrow mb-3">Services</p>
          <div className="mb-8 grid grid-cols-1 gap-1 sm:grid-cols-2">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="rounded-lg border border-slate-100 px-3 py-2.5 text-sm font-medium text-slate-800"
                data-testid={`mobile-service-${s.slug}`}
              >
                {s.name}
              </Link>
            ))}
          </div>
          <div className="flex flex-col gap-3">
            <SectionLink id="why" label="Why Fusion" className="!text-base" />
            <SectionLink id="regions" label="Regions" className="!text-base" />
            <SectionLink id="reviews" label="Reviews" className="!text-base" />
            <Link to="/contact" className="text-base font-semibold text-slate-800" data-testid="mobile-contact-link">
              Contact
            </Link>
          </div>
          <a
            href={`tel:${SITE.phoneEmergency}`}
            className="mt-8 flex items-center justify-center gap-2 rounded-full bg-brand-red px-4 py-3 text-sm font-bold text-white"
            data-testid="mobile-emergency-btn"
          >
            <Siren className="h-4 w-4" /> 24/7 emergency: {SITE.phoneEmergencyDisplay}
          </a>
        </div>
      )}
    </>
  );
}
